import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogStore} from '@ariakit/react'
import {FormProvider, useForm} from 'react-hook-form'
import {DeleteAgendaEventFormValidationSchema} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import type {AgendaEventAbsence} from '../../types'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {Text} from '@/components/ui/text/Text'
import {Trans, useTranslation} from '@/translations/i18n'
import {format} from 'date-fns'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useDeleteAgendaEvent} from '../../queries/useDeleteAgendaEvent'
import {toast} from '@/utilities/toast/toast'
import {errorHandler} from '@/utilities/helpers'
import {useGetEvent} from '../../queries/useGetEvent'
import {useGetValidatedAccesses} from '../../queries/useGetValidatedAccesses'

export const DeletePatientAbsenceDialog = ({
    event,
    dialogStore
}: {
    event: AgendaEventAbsence
    dialogStore: DialogStore
}) => {
    const {t} = useTranslation()
    const form = useForm<DeleteAgendaEventFormValidationSchema>({
        resolver: zodResolver(DeleteAgendaEventFormValidationSchema)
    })

    // The result of this query will be also grabbed from the cache in the useSocketAgendaEvents hook
    // So it is needed to track the event object from the socket hook
    const {data: eventData, isLoading: isEventLoading} = useGetEvent({
        eventId: event.id,
        responseType: 'extended',
        options: {
            enabled: !!event.id
        }
    })

    const eventPatient = eventData?.eventPatients?.[0]
    const deleteEventMutation = useDeleteAgendaEvent({
        urlParams: {eventId: event.id},
        options: {
            onSuccess: () => {
                toast.success(t('agenda:delete_patient_absence_dialog_success'))
                dialogStore.hide()
            },
            onError: errorHandler
        }
    })

    const onSubmit = form.handleSubmit(data => {
        deleteEventMutation.mutate({
            notificationRecipientIds: data.notificationsRecipients?.map(recipient => recipient.id),
            notificationDetails: data.notificationDetails
        })
    })

    const {data: retrieveValidatedEvents, isPending: isRetrieveValidatedEventsLoading} = useGetValidatedAccesses({
        params: {
            startDate: format(event.start, 'yyyy-MM-dd'),
            endDate: format(event.end, 'yyyy-MM-dd'),
            patient: eventPatient?.patientId,
            type: 'access'
        },
        options: {
            enabled: !!eventPatient?.patientId
        }
    })

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                confirmButtonDisabled={
                    (retrieveValidatedEvents?.length ?? 0) > 0 || isRetrieveValidatedEventsLoading || isEventLoading
                }
                confirmButtonTooltipContent={
                    (retrieveValidatedEvents?.length ?? 0) > 0
                        ? t('agenda:delete_patient_absence_dialog_tooltip')
                        : undefined
                }
                dialogStore={dialogStore}
                onConfirm={onSubmit}
                isConfirmPending={deleteEventMutation.isPending}
                confirmButtonVariant="primaryDanger"
                confirmButtonText={t('commons:delete')}
                headerSlot={
                    <Flexbox direction="column" gap={7} fullWidth>
                        <Flexbox direction="column" fullWidth gap={1}>
                            <DialogHeading>{t('agenda:delete_patient_absence_dialog_title')}</DialogHeading>
                            {isEventLoading ? (
                                <Skeleton width="200px" height="20px" />
                            ) : eventPatient && eventData ? (
                                <Text palette="neutral" shade="500" size="textSm">
                                    {eventPatient.patient?.fullName} -{' '}
                                    {eventPatient?.project ? (
                                        <Trans
                                            i18nKey="agenda:pri_n_expiration_date"
                                            values={{
                                                code: eventPatient.project?.code || '-',
                                                date: eventPatient.project?.expirationDate
                                                    ? format(
                                                          new Date(eventPatient.project.expirationDate),
                                                          'dd/MM/yyyy'
                                                      )
                                                    : '-'
                                            }}
                                        />
                                    ) : (
                                        '-'
                                    )}
                                </Text>
                            ) : (
                                <Text palette="neutral" shade="500" size="textSm">
                                    {eventPatient?.patient?.fullName}
                                </Text>
                            )}
                        </Flexbox>
                        <Grid columns={2} columnGap={6} rowGap={2} fullWidth>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:add_absence_start_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.start && format(event.start, 'dd/MM/yyyy')}
                            </Text>

                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:add_absence_end_date')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.end && format(event.end, 'dd/MM/yyyy')}
                            </Text>

                            <Text palette="neutral" shade="500" size="textSm">
                                {t('agenda:communication_absence')}
                            </Text>
                            <Text palette="neutral" shade="900" size="textSm" weight="500">
                                {event.communicatedOnTime !== null && event.communicatedOnTime
                                    ? t('commons:over_48_hours')
                                    : t('commons:within_48_hours')}
                            </Text>
                        </Grid>
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
